import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { BootstrapModalConfirm, Input } from "components/bootstrap";
import UserNotification from "util/UserNotification";
import { createTranslationFeedback } from "./api";
import { CATEGORIES } from "./types";
const NO_KEY = "none";
const Source = styled.code(
  ({ theme }) => css`
    display: block;
    white-space: pre-wrap;
    word-break: break-word;
    margin-top: ${theme.spacings.xxs};
  `
);
const Hint = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.gray[50]};
    font-size: ${theme.fonts.size.small};
  `
);
const candidateId = ({ namespace, key }) => `${namespace}:${key}`;
const resource = (language, namespace, key) => {
  const value = i18n.getResource(language, namespace, key);
  return typeof value === "string" ? value : void 0;
};
const isMissingTranslation = (candidate, language) => !candidate || candidate.usedLanguage !== language;
const defaultCategory = (candidate, language) => isMissingTranslation(candidate, language) && language !== "en" ? "untranslated" : "wrong";
const TranslationFeedbackModal = ({ target, onClose }) => {
  const { t } = useTranslation("translation_feedback");
  const { candidates, language } = target;
  const [selected, setSelected] = useState(candidates.length > 0 ? candidateId(candidates[0]) : NO_KEY);
  const candidate = candidates.find((other) => candidateId(other) === selected);
  const [text, setText] = useState(target.text);
  const [category, setCategory] = useState(defaultCategory(candidates[0], language));
  const [suggestion, setSuggestion] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const englishText = candidate ? resource("en", candidate.namespace, candidate.key) : void 0;
  const currentTranslation = candidate && language !== "en" ? resource(language, candidate.namespace, candidate.key) : void 0;
  const onSelectCandidate = (id) => {
    setSelected(id);
    setCategory(
      defaultCategory(
        candidates.find((other) => candidateId(other) === id),
        language
      )
    );
  };
  const onSubmit = () => {
    setIsSubmitting(true);
    createTranslationFeedback({
      language,
      namespace: candidate == null ? void 0 : candidate.namespace,
      key: candidate == null ? void 0 : candidate.key,
      text: text.trim() || target.text,
      english_text: englishText,
      suggestion,
      comment,
      category,
      path: target.path,
      dom_path: target.domPath
    }).then(() => {
      UserNotification.success(t("notifications.created"), t("notifications.created_title"));
      onClose();
    }).catch((error) => {
      UserNotification.error(t("notifications.create_failed", { error }), t("notifications.create_failed_title"));
      setIsSubmitting(false);
    });
  };
  return /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: true,
      title: t("modal.title"),
      onCancel: onClose,
      onConfirm: onSubmit,
      confirmButtonText: t("modal.submit"),
      confirmButtonDisabled: !text.trim() && !target.text,
      isAsyncSubmit: true,
      isSubmitting,
      submitLoadingText: t("modal.submitting")
    },
    /* @__PURE__ */ React.createElement("div", { "data-translation-feedback": true }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "translation-feedback-text",
        type: "textarea",
        label: t("modal.text"),
        help: t("modal.text_help"),
        rows: 2,
        value: text,
        onChange: (event) => setText(event.target.value)
      }
    ), /* @__PURE__ */ React.createElement(Input, { id: "translation-feedback-key", label: t("modal.key") }, /* @__PURE__ */ React.createElement(React.Fragment, null, candidates.map((option) => /* @__PURE__ */ React.createElement(
      Input,
      {
        key: candidateId(option),
        id: `translation-feedback-key-${candidateId(option)}`,
        type: "radio",
        name: "translation-feedback-key",
        value: candidateId(option),
        checked: selected === candidateId(option),
        onChange: () => onSelectCandidate(candidateId(option)),
        label: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("code", null, candidateId(option)), isMissingTranslation(option, language) && language !== "en" ? ` (${t("modal.not_translated")})` : "", !option.exact ? ` (${t("modal.partial_match")})` : "")
      }
    )), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "translation-feedback-key-none",
        type: "radio",
        name: "translation-feedback-key",
        value: NO_KEY,
        checked: selected === NO_KEY,
        onChange: () => onSelectCandidate(NO_KEY),
        label: candidates.length > 0 ? t("modal.none_of_these") : t("modal.no_key_found")
      }
    ))), englishText && /* @__PURE__ */ React.createElement(Hint, null, t("modal.english"), /* @__PURE__ */ React.createElement(Source, null, englishText), currentTranslation && /* @__PURE__ */ React.createElement(React.Fragment, null, t("modal.current_translation"), /* @__PURE__ */ React.createElement(Source, null, currentTranslation))), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "translation-feedback-category",
        type: "select",
        label: t("modal.category"),
        value: category,
        onChange: (event) => setCategory(event.target.value)
      },
      CATEGORIES.map((option) => /* @__PURE__ */ React.createElement("option", { key: option, value: option }, t(`categories.${option}`)))
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "translation-feedback-suggestion",
        type: "textarea",
        label: t("modal.suggestion"),
        help: t("modal.suggestion_help"),
        rows: 2,
        value: suggestion,
        onChange: (event) => setSuggestion(event.target.value)
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "translation-feedback-comment",
        type: "textarea",
        label: t("modal.comment"),
        rows: 2,
        value: comment,
        onChange: (event) => setComment(event.target.value)
      }
    ))
  );
};
export default TranslationFeedbackModal;
